import { useEffect, useRef } from 'react';
import {
  FieldValues,
  UseFormHandleSubmit,
  UseFormWatch,
} from 'react-hook-form';

interface UseSubmitOnChangeArgs<T extends FieldValues> {
  enabled?: boolean;
  onChange: (values: T) => void;
  watch: UseFormWatch<T>;
  handleSubmit: UseFormHandleSubmit<T, any>;
  isFormDirty?: boolean;
}

export const useSubmitOnChange = <T extends FieldValues>({
  enabled,
  onChange,
  handleSubmit,
  watch,
  isFormDirty,
}: UseSubmitOnChangeArgs<T>) => {
  // Keep onChange and handleSubmit in refs so changing their identity (e.g.
  // when a mutation's status updates after a save) does not re-run this
  // effect. Without this the effect cleanup fires while a debounce timer is
  // still pending, causing a premature save with partial form values.
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;
  const handleSubmitRef = useRef(handleSubmit);
  handleSubmitRef.current = handleSubmit;

  useEffect(() => {
    if (!enabled) return;

    // Snapshot the refs at effect-start so the cleanup always calls the handlers
    // that were active when this effect ran, not whatever the refs hold at
    // teardown time (which may belong to a later render).
    const cleanupHandleSubmit = handleSubmitRef.current;
    const cleanupOnChange = onChangeRef.current;
    let timer: ReturnType<typeof setTimeout> | undefined;
    const subscription = watch(() => {
      if (timer !== undefined) clearTimeout(timer);

      timer = setTimeout(() => {
        timer = undefined;
        if (isFormDirty !== false) handleSubmitRef.current(onChangeRef.current)();
      }, 300);
    });

    return () => {
      subscription.unsubscribe();
      if (timer !== undefined) {
        clearTimeout(timer);
        if (isFormDirty !== false) cleanupHandleSubmit(cleanupOnChange)();
      }
    };
  }, [watch, enabled, isFormDirty]);
};
